import { Helmet } from 'react-helmet-async';
import { LegalPageLayout } from '../components/legal/LegalPageLayout';
import { HighlightBox } from '../components/legal/HighlightBox';

export const PrivacyPage = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy — Solo Sense</title>
        <meta name="description" content="Learn how Solo Sense protects your privacy. No data collection, no tracking, no stored recordings. Privacy-first spatial transcription for Apple Vision Pro." />
        <link rel="canonical" href="https://solosense.ai/privacy" />
      </Helmet>
    <LegalPageLayout
      title="Privacy Policy"
      meta="Effective Date: January 04, 2026 · Last Updated: January 04, 2026"
    >
      <h2>1. Introduction</h2>
      <p>SOLO SENSE ("we," "our," or "us") is a mobile application designed for Apple Vision Pro that provides movable subtitles, captioning, and translation to meet individual user accessibility needs. Privacy, dignity, and user control are core to our design.</p>

      <h2>2. What SOLO SENSE Does</h2>
      <p>SOLO SENSE enables real-time, user-controlled captioning and translation in environments such as:</p>
      <ul>
        <li>Live events and performances</li>
        <li>Public spaces</li>
        <li>HIPAA-compliant medical facilities</li>
        <li>Federal, state, or local government agencies</li>
        <li>Educational and workplace settings</li>
      </ul>

      <h2>3. Data We Do Not Collect</h2>
      <p>We intentionally minimize data use. We do not:</p>
      <ul>
        <li>Store audio recordings by default</li>
        <li>Store captions or translations</li>
        <li>Collect biometric data</li>
        <li>Track precise location</li>
        <li>Collect medical records or PHI</li>
        <li>Sell, rent, or monetize user data</li>
        <li>Use data for advertising or profiling</li>
      </ul>

      <h2>4. Data Temporarily Processed</h2>
      <p>When enabled by the user, SOLO SENSE may temporarily process:</p>
      <ul>
        <li>Audio input (for captioning/translation)</li>
        <li>Text output (displayed locally)</li>
      </ul>
      <p>Processing is session-based, transient, and automatically discarded after use.</p>

      <h2>5. User Consent & Control</h2>
      <ul>
        <li>Audio and translation features are opt-in</li>
        <li>Users may stop processing at any time</li>
        <li>No background listening occurs</li>
        <li>Users decide when and where SOLO SENSE is used</li>
      </ul>

      <h2>6. Third-Party APIs & Translation Services</h2>
      <p>SOLO SENSE may use third-party speech-to-text and translation APIs to deliver best-in-class accuracy. These services:</p>
      <ul>
        <li>Process data only to provide requested features</li>
        <li>Do not retain data beyond session processing</li>
        <li>Are contractually restricted from secondary use</li>
        <li>Meet strong privacy and security standards</li>
      </ul>

      <h2>7. HIPAA & Sensitive Environments</h2>
      <ul>
        <li>SOLO SENSE does not store or manage PHI</li>
        <li>No patient records are accessed</li>
        <li>Audio/text is transient and user-controlled</li>
      </ul>
      <p>SOLO SENSE functions as an accessibility aid, not a medical data system.</p>

      <h2>8. Government & Regulated Use</h2>
      <ul>
        <li>No surveillance functionality</li>
        <li>No automatic data sharing with agencies</li>
        <li>No retention unless explicitly enabled</li>
      </ul>

      <h2>9. Security</h2>
      <p>We apply reasonable safeguards including:</p>
      <ul>
        <li>Encrypted processing (where applicable)</li>
        <li>Minimal permission access</li>
        <li>Secure platform APIs</li>
      </ul>

      <h2>10. Children's Privacy</h2>
      <p>Not intended for children under 13 without parental consent.</p>

      <h2>11. Contact</h2>
      <p>
        <strong>SOLO SENSE</strong><br />
        Email: <a href="mailto:privacy@solosense.ai">privacy@solosense.ai</a><br />
        Website: <a href="https://www.solosense.ai">www.solosense.ai</a>
      </p>

      <HighlightBox title="Apple App Store Privacy Nutrition Label">
        <p><strong>Data Collection:</strong> No data collected for tracking. No data linked to user identity.</p>
        <p><strong>Data Used to Provide App Functionality:</strong> Audio (temporary, user-initiated) and transcribed text (temporary).</p>
        <p><strong>Data Retention:</strong> None by default. Session-only processing.</p>
        <p><strong>Third-Party Access:</strong> Limited to functional APIs only. No advertising or analytics use.</p>
      </HighlightBox>

      <HighlightBox title="Translation & Captioning Consent">
        <p>When you enable captioning or translation, SOLO SENSE may temporarily process audio and text using on-device or third-party services to provide real-time subtitles.</p>
        <ul>
          <li>No recordings are stored</li>
          <li>Data is not used for advertising or training</li>
          <li>Processing stops immediately when disabled</li>
        </ul>
        <p>By enabling this feature, you consent to this temporary processing.</p>
      </HighlightBox>

      <HighlightBox title="HIPAA / Enterprise Privacy Addendum">
        <ul>
          <li>SOLO SENSE does not function as a Business Associate unless explicitly contracted</li>
          <li>No PHI is intentionally collected, stored, or transmitted</li>
          <li>All processing is user-initiated and transient</li>
          <li>SOLO SENSE may be deployed as an accessibility accommodation tool</li>
        </ul>
        <p>Enterprise administrators are responsible for internal compliance policies.</p>
      </HighlightBox>
    </LegalPageLayout>
    </>
  );
};
